import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LiveGenerationPlanLoadingComponent } from '@foxera/shared/components/live-generation-plan-loading/live-generation-plan-loading.component';
import { PlanTagsComponent } from '@foxera/feature/clients/components/plan-tags/plan-tags.component';
import { buildWorkoutPlanUpdatePayload, deepCloneWorkoutPlan } from '@foxera/feature/clients/mappers/sport.mappers';
import { ClientSearchExercisesComponent } from '@foxera/shared/components/client-search-exercises/client-search-exercises.component';
import { ClientService } from '@foxera/feature/clients/services/client.service';
import { WorkoutJobsStateService } from '@foxera/shared/services/workout-jobs-state.service';
import { WorkoutInfoService } from '@foxera/feature/clients/services/workout-info.service';
import { ClientViewExerciseComponent } from '@foxera/shared/components/client-view-exercise/client-view-exercise.component';
import { FoxeraButtonLoadingComponent } from '@foxera/shared/components/foxera-button-loading/foxera-button-loading.component';
import { GlobalLoadingComponent } from '@foxera/shared/components/foxera-loading/foxera-loading.component';
import { FoxeraTabsModernComponent, TabOption } from '@foxera/shared/components/foxera-tabs-modern/foxera-tabs-modern.component';
import { FoxeraEditDialogComponent } from '@foxera/shared/components/foxera-edit-dialog/foxera-edit-dialog.component';
import { FoxeraToggleSwitchComponent } from '@foxera/shared/components/foxera-toggle-switch/foxera-toggle-switch.component';
import { FoxeraDrawerBottomComponent } from '@foxera/shared/components/foxera-drawer-bottom/foxera-drawer-bottom.component';
import { FoxeraDrawerFullComponent } from '@foxera/shared/components/foxera-drawer-full/foxera-drawer-full.component';
import { IExerciseSearchItem, IWorkoutInfoResponse, IWorkoutPlanResponse } from '@foxera/shared/interfaces/client.interface';
import { TrainingType } from '@foxera/feature/clients/constants/workout.constants';
import { CustomToastService } from '@foxera/shared/services/custom-toast.service';
import { PermissionService } from '@foxera/shared/services/permission.service';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Popover, PopoverModule } from 'primeng/popover';
import { SliderModule } from 'primeng/slider';
type IExercise = IWorkoutPlanResponse['workoutPlan']['days'][number]['structure'][number]['exercises'][number];
type SessionNoteHighlight = 'highest' | 'moderate' | 'minimal';
type SessionNotePart = { text: string; highlight: SessionNoteHighlight | null };

const SESSION_NOTE_HIGHLIGHT_REGEX = /\b(highest|moderate|minimal)\b/i;
const SESSION_NOTE_HIGHLIGHT_CLASSES: Record<SessionNoteHighlight, string> = {
  highest: 'font-semibold text-red-800 bg-red-100 px-1 rounded',
  moderate: 'font-semibold text-orange-800 bg-orange-100 px-1 rounded',
  minimal: 'font-semibold text-green-800 bg-green-100 px-1 rounded'
};

@Component({
  selector: 'app-client-plan',
  standalone: true,
  templateUrl: './client-plan.component.html',
  styleUrls: ['./client-plan.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    ButtonModule,
    DialogModule,
    SliderModule,
    PopoverModule,
    LiveGenerationPlanLoadingComponent,
    PlanTagsComponent,
    FoxeraTabsModernComponent,
    ClientViewExerciseComponent,
    FoxeraButtonLoadingComponent,
    GlobalLoadingComponent,
    ClientSearchExercisesComponent,
    FoxeraToggleSwitchComponent,
    FoxeraEditDialogComponent,
    FoxeraDrawerBottomComponent,
    FoxeraDrawerFullComponent
  ]
})
export class ClientPlanComponent implements OnChanges {
  readonly responsiveService = inject(ResponsiveService);
  
  drawerVisible = signal<boolean>(true); // Drawer visibility
  showDetailsDrawer = signal<boolean>(false); // Client details drawer
  showAiNotesDrawer = signal<boolean>(false); // AI notes drawer
  showExerciseActionsDrawer = signal<boolean>(false); // Exercise actions drawer
  
  @Input() clientId: number = 0;
  @Input() planId?: number;
  /**
   * When set, the component shows the live loading animation for this existing
   * job instead of triggering a new generation.
   */
  @Input() activeJobId?: string;
  @Input() trainingType?: string;
  @Input() branchId?: string;
  @Output() close = new EventEmitter<void>();
  @Output() planSaved = new EventEmitter<void>();
  /** Emitted when the user hides the loading screen to work in the background */
  @Output() hiddenInBackground = new EventEmitter<void>();

  planBranchId = computed(() =>
    this.branchId
    || this.workoutPlan()?.branchId
    || this.workoutInfo()?.currentBranchId
  );

  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  workoutPlan = signal<IWorkoutPlanResponse | null>(null);
  modifiedWorkoutPlan = signal<IWorkoutPlanResponse | null>(null);
  workoutInfo = signal<IWorkoutInfoResponse | null>(null);
  selectedDayTab = signal<string>('1');
  dayTabs = signal<TabOption[]>([]);
  isAddMode = signal<boolean>(false);
  
  hasChanges = computed(() => {
    const original = this.workoutPlan();
    const modified = this.modifiedWorkoutPlan();
    
    if (!original || !modified) return false;
    return JSON.stringify(original.workoutPlan) !== JSON.stringify(modified.workoutPlan);
  });

  // Computed total time for selected day based on section durations
  selectedDayTotalTime = computed(() => {
    const selectedDay = this.getSelectedDay();
    if (!selectedDay || !selectedDay.structure) return 0;

    const total = selectedDay.structure.reduce((sum, section) => {
      return sum + section.durationInMinutes;
    }, 0);

    return Math.round(total);
  });

  /**
   * Format total time in a readable way (e.g., "45 min" or "1h 30min")
   */
  getFormattedTotalTime(): string {
    const totalMinutes = this.selectedDayTotalTime();
    
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (minutes === 0) {
      return `${hours}h`;
    }
    
    return `${hours}h ${minutes}min`;
  }
  
  isExerciseDialogVisible = signal<boolean>(false);
  selectedExercise = signal<IExercise | null>(null);
  selectedExerciseContext = signal<{ sectionTitle: string; exerciseIndex: number } | null>(null);

  @ViewChild('exerciseActionsPopover') exerciseActionsPopover!: Popover;
  selectedExerciseForActions = signal<{ sectionTitle: string; exerciseId: string; exerciseIndex: number } | null>(null);
  isSearchDialogVisible = signal<boolean>(false);
  searchMode = signal<'add' | 'switch'>('add');
  selectedSectionForSearch = signal<string | null>(null);
  selectedExerciseMovementPatterns = signal<string[]>([]);
  
  duplicateExerciseIds = signal<Set<string>>(new Set());
  duplicateErrorMessage = signal<string | null>(null);
  isReviewed = signal<boolean>(false);

  // Section expansion state - warmup starts expanded by default
  expandedSections = signal<Set<string>>(new Set(['Warmup', 'warmup', 'WARMUP']));

  // Section duration editing with dialog
  isDurationDialogVisible = signal<boolean>(false);
  editingSectionTitle = signal<string>('');
  editingSectionDay = signal<number>(0);
  tempDurationValue = signal<number>(0);

  // Day title editing
  isDayTitleDialogVisible = signal<boolean>(false);
  editingDayNumber = signal<number>(0);
  tempDayTitle = signal<string>('');

  constructor(
    private workoutInfoService: WorkoutInfoService,
    private clientService: ClientService,
    private customToastService: CustomToastService,
    private permissionService: PermissionService,
    private workoutJobsStateService: WorkoutJobsStateService
  ) {
    // When reconnecting to an existing active job, watch for its completion
    effect(() => {
      // Explicitly read jobs signal to make effect reactive to job updates
      const jobs = this.workoutJobsStateService.jobs();
      const activeJobId = this.activeJobId;
      const isLoading = this.isLoading();
      
      if (!activeJobId || !isLoading) {
        return;
      }

      const job = jobs.find((j) => j.jobId === activeJobId);

      if (job?.status === 'Completed' && job.workoutPlanId != null) {
        // Job finished – load the resulting plan automatically
        this.workoutInfoService.getWorkoutPlanById(job.workoutPlanId).subscribe({
          next: (response) => {
            this.workoutPlan.set(response);
            this.modifiedWorkoutPlan.set(this.mapWorkoutPlanWithCalculatedDurations(response));
            this.initializeDayTabs(response);
            this.isReviewed.set(response.isReviewed);
            this.isAddMode.set(false);
            this.isLoading.set(false);
          },
          error: () => {
            this.isLoading.set(false);
          },
        });
      } else if (job?.status === 'Failed' || job?.status === 'Cancelled') {
        this.isLoading.set(false);
        this.close.emit();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planId'] && this.planId) {
      this.loadWorkoutPlan();
    } else if (changes['activeJobId'] && this.activeJobId) {
      this.loadWorkoutPlan();
    } else if (changes['clientId'] && this.clientId) {
      this.loadWorkoutPlan();
    }
  }

  private loadWorkoutPlan(): void {
    this.isLoading.set(true);

    // Reconnect mode – show the loading animation while waiting for the existing job
    if (this.activeJobId && !this.planId) {
      this.isAddMode.set(true);
        this.clientService.getClientWorkoutInfo(this.clientId).subscribe({
          next: (workoutInfoResponse) => {
            this.workoutInfo.set(workoutInfoResponse);
          },
          error: () => { /* non-critical – animation still plays */ },
        });
      return;
    }

    if (this.planId) {
      this.isAddMode.set(false);
      // Fetch actual client workout info only if clientId is valid
      if (this.clientId > 0) {
        this.clientService.getClientWorkoutInfo(this.clientId).subscribe({
          next: (workoutInfoResponse) => {
            this.workoutInfo.set(workoutInfoResponse);
          },
          error: () => { /* non-critical – UI still works */ },
        });
      }
      
      this.workoutInfoService.getWorkoutPlanById(this.planId!).subscribe({
        next: (response) => {
          this.workoutPlan.set(this.mapWorkoutPlanWithCalculatedDurations(response));
          this.modifiedWorkoutPlan.set(deepCloneWorkoutPlan(response));
          this.initializeDayTabs(response);
          this.isReviewed.set(response.isReviewed);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.workoutPlan.set(null);
          }
        }); 
    } else {
      this.isAddMode.set(true);
      this.clientService.getClientWorkoutInfo(this.clientId).subscribe({
        next: (workoutInfoResponse) => {
          this.workoutInfo.set(workoutInfoResponse);
          this.startWorkoutPlanGeneration(workoutInfoResponse);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    }
  }

  private startWorkoutPlanGeneration(workoutInfoResponse: IWorkoutInfoResponse): void {
    const branchId = this.branchId || workoutInfoResponse.currentBranchId;
    const trainingType = this.trainingType || workoutInfoResponse.currentTrainingType || TrainingType.Standard;

    if (!branchId) {
      this.isLoading.set(false);
      this.customToastService.showError('Branch is required to generate a workout plan');
      return;
    }

    this.workoutInfoService.generateWorkoutPlan(this.clientId, trainingType).subscribe({
      next: (response) => {
        this.activeJobId = response.jobId;
      },
      error: () => {
        this.isLoading.set(false);
        this.workoutPlan.set(null);
        this.modifiedWorkoutPlan.set(null);
      }
    });
  }

  private initializeDayTabs(plan: IWorkoutPlanResponse): void {
    const tabs: TabOption[] = plan.workoutPlan.days.map(day => ({
      value: day.day.toString(),
      label: `Day ${day.day}`
    }));
    this.dayTabs.set(tabs);
    this.selectedDayTab.set('1');
  }

  onDayTabChange(dayValue: string): void {
    this.selectedDayTab.set(dayValue);
  }

  getSelectedDay() {
    const plan = this.modifiedWorkoutPlan() ?? this.workoutPlan();
    if (!plan) return null;
    
    const dayNumber = parseInt(this.selectedDayTab());
    return plan.workoutPlan.days.find(day => day.day === dayNumber) || null;
  }

  getSessionNoteParts(notes: string): SessionNotePart[] {
    const parts: SessionNotePart[] = [];
    const paragraphs = notes.split('\n');

    paragraphs.forEach((paragraph, paragraphIndex) => {
      if (paragraphIndex > 0) {
        parts.push({ text: '\n', highlight: null });
      }

      const match = paragraph.match(SESSION_NOTE_HIGHLIGHT_REGEX);

      if (!match || match.index === undefined) {
        parts.push({ text: paragraph, highlight: null });
        return;
      }

      if (match.index > 0) {
        parts.push({ text: paragraph.slice(0, match.index), highlight: null });
      }
      parts.push({
        text: match[0],
        highlight: match[1].toLowerCase() as SessionNoteHighlight
      });
      if (match.index + match[0].length < paragraph.length) {
        parts.push({ text: paragraph.slice(match.index + match[0].length), highlight: null });
      }
    });

    return parts.length ? parts : [{ text: notes, highlight: null }];
  }

  getSessionNoteHighlightClass(highlight: SessionNoteHighlight | null): string {
    return highlight ? SESSION_NOTE_HIGHLIGHT_CLASSES[highlight] : '';
  }

  onClose(): void {
    this.close.emit();
  }

  onHideInBackground(): void {
    this.hiddenInBackground.emit();
    this.close.emit();
  }

  onDone(): void {
    if (!this.planId) {
      this.planSaved.emit();
    }
    this.onClose();
  }

    getGoalIcon(goal: string): string {
    const icons: Record<string, string> = {
      'BuildMuscles': '💪',
      'LoseFat': '🔥',
      'ToneBody': '✨',
      'IncreaseStrength': '🏋️',
      'ImproveEndurance': '🏃'
    };
    return icons[goal] || '🎯';
  }

  getFitnessLevelIcon(level: string): string {
    const icons: Record<string, string> = {
      'Beginner': '🌱',
      'Intermediate': '📈',
      'Advanced': '🚀'
    };
    return icons[level] || 'pi-chart-line';
  }

  getSectionIcon(title: string): string {
    const normalized = title.toLowerCase().replace(/[\s-]/g, '');
    const iconMap: Record<string, string> = {
      'warmup': 'pi-sun',
      'resistancetraining': 'pi-bolt',
      'cardio': 'pi-heart',
      'cooldown': 'pi-moon'
    };
    
    return iconMap[normalized] || 'pi-circle';
  }

  getSectionClass(title: string): string {
    const normalized = title.toLowerCase().replace(/[\s-]/g, '');
    const classMap: Record<string, string> = {
      'warmup': 'warmup',
      'resistancetraining': 'resistance-training',
      'cardio': 'cardio',
      'cooldown': 'cooldown'
    };
    
    return classMap[normalized] || '';
  }

  /**
   * Calculate section duration based on exercises' avgDuration
   * Only for Resistance Training sections
   */
  private calculateResistanceTrainingDuration(exercises: IExercise[]): number {
    const totalMinutes = exercises.reduce((total, exercise) => {
      if (exercise.avgDuration && exercise.avgDuration > 0) {
        return total + exercise.avgDuration;
      }
      return total;
    }, 0);
    
    // Round up to the nearest integer
    return Math.ceil(totalMinutes);
  }

  /**
   * Compute an exercise's contribution to its section duration, in seconds.
   * - Time-based exercises contribute their totalTimeSec directly.
   * - Reps-based exercises contribute totalReps * repDurationSec (per-rep duration).
   * When the isTimeBased flag is missing, whichever value is set is used.
   * Per-side exercises (isPerSide) are performed on both sides, so their duration is doubled.
   */
  private getExerciseDurationSeconds(exercise: IExercise): number {
    const seconds = this.getExerciseBaseDurationSeconds(exercise);
    return exercise.isPerSide ? seconds * 2 : seconds;
  }

  /**
   * Base per-exercise duration in seconds before applying the per-side multiplier.
   */
  private getExerciseBaseDurationSeconds(exercise: IExercise): number {
    if (exercise.isTimeBased === true) {
      return exercise.totalTimeSec ?? 0;
    }
    if (exercise.isTimeBased === false) {
      return (exercise.totalReps ?? 0) * (exercise.repDurationSec ?? 0);
    }
    // Fallback for exercises without the flag: prefer whichever field is set
    if (exercise.totalTimeSec != null && exercise.totalTimeSec > 0) {
      return exercise.totalTimeSec;
    }
    if (exercise.totalReps != null && exercise.totalReps > 0) {
      return exercise.totalReps * (exercise.repDurationSec ?? 0);
    }
    return 0;
  }

  /**
   * Calculate a time/reps section's duration (Warmup, Cardio, Cooldown) as the sum of
   * each exercise's time - totalTimeSec for time-based, totalReps * repDurationSec for
   * reps-based - converted to minutes. These durations are read-only and always derived
   * from the exercise data.
   */
  private calculateTimeAndRepsDuration(exercises: IExercise[], sectionTitle: string): number {
    const isWarmup = this.getSectionClass(sectionTitle) === 'warmup';

    let totalSeconds = 0;
    const exerciseSteps = exercises.map((exercise, index) => {
      const baseSeconds = this.getExerciseBaseDurationSeconds(exercise);
      const exerciseSeconds = exercise.isPerSide ? baseSeconds * 2 : baseSeconds;
      totalSeconds += exerciseSeconds;

      return {
        index: index + 1,
        name: exercise.exerciseName,
        isTimeBased: exercise.isTimeBased,
        totalTimeSec: exercise.totalTimeSec,
        totalReps: exercise.totalReps,
        repDurationSec: exercise.repDurationSec,
        isPerSide: exercise.isPerSide,
        baseSeconds,
        exerciseSeconds,
        baseFormula: this.getExerciseBaseDurationFormula(exercise),
        perSideFormula: exercise.isPerSide ? `${baseSeconds} x 2 = ${exerciseSeconds}` : null,
      };
    });

    const durationMinutes = this.convertSecondsToDurationMinutes(totalSeconds, sectionTitle);

    return durationMinutes;
  }

  private getExerciseBaseDurationFormula(exercise: IExercise): string {
    if (exercise.isTimeBased === true) {
      return `totalTimeSec (${exercise.totalTimeSec ?? 0})`;
    }
    if (exercise.isTimeBased === false) {
      return `totalReps (${exercise.totalReps ?? 0}) x repDurationSec (${exercise.repDurationSec ?? 0})`;
    }
    if (exercise.totalTimeSec != null && exercise.totalTimeSec > 0) {
      return `fallback totalTimeSec (${exercise.totalTimeSec})`;
    }
    if (exercise.totalReps != null && exercise.totalReps > 0) {
      return `fallback totalReps (${exercise.totalReps}) x repDurationSec (${exercise.repDurationSec ?? 0})`;
    }
    return 'no time/reps data => 0';
  }

  /**
   * Convert a total seconds value to duration minutes.
   * Warmup uses: floor to minutes, then +1 only when the remainder exceeds 30 seconds.
   * Cardio/Cooldown use standard rounding to the nearest minute.
   */
  private convertSecondsToDurationMinutes(totalSeconds: number, sectionTitle: string): number {
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    if (this.getSectionClass(sectionTitle) === 'warmup') {
      return remainingSeconds > 30 ? minutes + 1 : minutes;
    }

    return Math.round(totalSeconds / 60);
  }

  /**
   * Sections whose duration is always computed in real time from their exercises'
   * time/reps data rather than being editable: Warmup, Cardio and Cooldown.
   */
  private isCalculatedTimeSection(sectionTitle: string): boolean {
    const sectionClass = this.getSectionClass(sectionTitle);
    return sectionClass === 'warmup' || sectionClass === 'cardio' || sectionClass === 'cooldown';
  }

  /**
   * Get section duration - simply returns durationInMinutes
   */
  getSectionDuration(section: { title: string; durationInMinutes: number; exercises: IExercise[] }): number {
    return section.durationInMinutes;
  }

  getTotalExercises(structure: { exercises: any[] }[]): number {
    return structure.reduce((total, section) => total + section.exercises.length, 0);
  }

  /**
   * Warmup, Cardio and Cooldown sections display totalTimeSec/totalReps on the exercise
   * card instead of the calculated avgDuration.
   */
  isTimeOrRepsSection(sectionTitle: string): boolean {
    return this.isCalculatedTimeSection(sectionTitle);
  }

  /**
   * Returns the exercise's totalTimeSec or totalReps as display text, using the backend's
   * isTimeBased flag to decide which field applies. Falls back to "whichever is set"
   * for exercises that predate that flag.
   * totalTimeSec is in seconds and is formatted as "x min x sec".
   * Per-side exercises (isPerSide) get a " x 2" suffix since they are done on both sides.
   */
  getExerciseTimeOrRepsText(exercise: IExercise): string | null {
    const baseText = this.getExerciseBaseTimeOrRepsText(exercise);
    if (baseText === null) {
      return null;
    }
    return exercise.isPerSide ? `${baseText} x 2` : baseText;
  }

  private getExerciseBaseTimeOrRepsText(exercise: IExercise): string | null {
    if (exercise.isTimeBased === true) {
      return exercise.totalTimeSec && exercise.totalTimeSec > 0 ? this.formatSecondsAsMinSec(exercise.totalTimeSec) : null;
    }
    if (exercise.isTimeBased === false) {
      return exercise.totalReps && exercise.totalReps > 0 ? `${exercise.totalReps} reps` : null;
    }
    if (exercise.totalTimeSec != null && exercise.totalTimeSec > 0) {
      return this.formatSecondsAsMinSec(exercise.totalTimeSec);
    }
    if (exercise.totalReps != null && exercise.totalReps > 0) {
      return `${exercise.totalReps} reps`;
    }
    return null;
  }

  /**
   * Formats a duration in seconds as "x min x sec", omitting whichever part is 0
   */
  private formatSecondsAsMinSec(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    if (minutes > 0 && seconds > 0) {
      return `${minutes} min ${seconds} sec`;
    }
    if (minutes > 0) {
      return `${minutes} min`;
    }
    return `${seconds} sec`;
  }

  /**
   * Check if a section's duration is editable (not Resistance Training, Warmup, Cardio or Cooldown).
   * Cardio's duration is always calculated from its exercises' totalTimeSec.
   */
  isSectionDurationEditable(sectionTitle: string): boolean {
    const sectionClass = this.getSectionClass(sectionTitle);
    return sectionClass !== 'resistance-training' && sectionClass !== 'warmup' && sectionClass !== 'cooldown' && sectionClass !== 'cardio';
  }

  /**
   * Check if a section is expanded
   */
  isSectionExpanded(sectionTitle: string): boolean {
    return this.expandedSections().has(sectionTitle);
  }

  /**
   * Toggle section expansion (for warmup)
   */
  toggleSectionExpansion(sectionTitle: string): void {
    const currentExpanded = new Set(this.expandedSections());
    if (currentExpanded.has(sectionTitle)) {
      currentExpanded.delete(sectionTitle);
    } else {
      currentExpanded.add(sectionTitle);
    }
    this.expandedSections.set(currentExpanded);
  }

  /**
   * Start editing a section's duration - opens dialog
   */
  onEditSectionDuration(dayNumber: number, sectionTitle: string, currentDuration: number): void {
    this.editingSectionDay.set(dayNumber);
    this.editingSectionTitle.set(sectionTitle);
    this.tempDurationValue.set(currentDuration);
    this.isDurationDialogVisible.set(true);
  }

  /**
   * Cancel editing a section's duration - closes dialog
   */
  onCancelEditDuration(): void {
    this.isDurationDialogVisible.set(false);
    this.editingSectionDay.set(0);
    this.editingSectionTitle.set('');
    this.tempDurationValue.set(0);
  }

  /**
   * Save the edited duration
   */
  onSaveSectionDuration(value: number | string): void {
    const newDuration = typeof value === 'number' ? value : parseInt(value.toString());
    const dayNumber = this.editingSectionDay();
    const sectionTitle = this.editingSectionTitle();

    // Validate duration
    if (newDuration <= 0 || isNaN(newDuration)) {
      this.customToastService.showError('Duration must be greater than 0');
      return;
    }

    // Update the section duration
    this.updateSectionDuration(dayNumber, sectionTitle, newDuration);

    // Close dialog
    this.isDurationDialogVisible.set(false);
    this.editingSectionDay.set(0);
    this.editingSectionTitle.set('');
    this.tempDurationValue.set(0);
  }

  /**
   * Update section duration in the modified workout plan
   */
  private updateSectionDuration(dayNumber: number, sectionTitle: string, newDuration: number): void {
    const plan = this.modifiedWorkoutPlan();
    if (!plan) return;

    const dayIndex = plan.workoutPlan.days.findIndex(d => d.day === dayNumber);
    if (dayIndex < 0) return;

    const day = plan.workoutPlan.days[dayIndex];
    const sectionIndex = day.structure.findIndex(s => s.title === sectionTitle);
    if (sectionIndex < 0) return;

    const section = day.structure[sectionIndex];
    
    // Update the section with new duration and recalculate exercise durations if editable
    let updatedSection = { ...section, durationInMinutes: newDuration };
    
    if (this.isSectionDurationEditable(sectionTitle) && section.exercises.length > 0) {
      const individualDuration = newDuration / section.exercises.length;
      const roundedDuration = Math.round(individualDuration * 10) / 10;
      
      // Update avgDuration for each exercise
      updatedSection.exercises = section.exercises.map(ex => ({
        ...ex,
        avgDuration: roundedDuration
      }));
    }
    
    const newStructure = day.structure.map((s, idx) =>
      idx === sectionIndex ? updatedSection : s
    );

    const newDay = { ...day, structure: newStructure };
    const newDays = plan.workoutPlan.days.map((d, idx) =>
      idx === dayIndex ? newDay : d
    );

    // Update signal with new plan
    this.modifiedWorkoutPlan.set({
      ...plan,
      workoutPlan: {
        ...plan.workoutPlan,
        days: newDays,
      },
    });
  }

  getHealthNotes(): string {
    const plan = this.workoutPlan();
    if (!plan) return '';
    
    const pains = plan.clientDetails.pains || [];
    const postureProblems = plan.clientDetails.postureProblems || [];
    
    return [...pains, ...postureProblems].join(', ');
  }

  hasHealthNotes(): boolean {
    const plan = this.workoutPlan();
    if (!plan) return false;
    const hasPains = plan.clientDetails.pains && plan.clientDetails.pains.length > 0;
    const hasPosture = plan.clientDetails.postureProblems && plan.clientDetails.postureProblems.length > 0;
    return hasPains || hasPosture;
  }

  /**
   * Start editing a day title - opens dialog
   */
  onEditDayTitle(dayNumber: number, currentTitle: string): void {
    this.editingDayNumber.set(dayNumber);
    this.tempDayTitle.set(currentTitle);
    this.isDayTitleDialogVisible.set(true);
  }

  /**
   * Cancel editing a day title - closes dialog
   */
  onCancelEditDayTitle(): void {
    this.isDayTitleDialogVisible.set(false);
    this.editingDayNumber.set(0);
    this.tempDayTitle.set('');
  }

  /**
   * Save the edited day title
   */
  onSaveDayTitle(value: number | string): void {
    const newTitle = value.toString().trim();
    const dayNumber = this.editingDayNumber();

    // Update the day title (validation is handled by the input component)
    this.updateDayTitle(dayNumber, newTitle);

    // Close dialog
    this.isDayTitleDialogVisible.set(false);
    this.editingDayNumber.set(0);
    this.tempDayTitle.set('');
  }

  /**
   * Update day title in the modified workout plan
   */
  private updateDayTitle(dayNumber: number, newTitle: string): void {
    const plan = this.modifiedWorkoutPlan();
    if (!plan) return;

    const dayIndex = plan.workoutPlan.days.findIndex(d => d.day === dayNumber);
    if (dayIndex < 0) return;

    const day = plan.workoutPlan.days[dayIndex];
    const newDay = { ...day, title: newTitle };
    const newDays = plan.workoutPlan.days.map((d, idx) =>
      idx === dayIndex ? newDay : d
    );

    // Update signal with new plan
    this.modifiedWorkoutPlan.set({
      ...plan,
      workoutPlan: {
        ...plan.workoutPlan,
        days: newDays,
      },
    });
  }

  onExerciseClick(exercise: IExercise, sectionTitle: string, exerciseIndex: number): void {
    this.selectedExercise.set(exercise);
    this.selectedExerciseContext.set({ sectionTitle, exerciseIndex });
    this.isExerciseDialogVisible.set(true);
  }

  onExerciseDialogClose(): void {
    this.isExerciseDialogVisible.set(false);
    this.selectedExercise.set(null);
    this.selectedExerciseContext.set(null);
  }

  onExerciseTotalTimeChanged(totalTimeSec: number): void {
    this.updateSelectedExerciseField('totalTimeSec', totalTimeSec);
  }

  onExerciseTotalRepsChanged(totalReps: number): void {
    this.updateSelectedExerciseField('totalReps', totalReps);
  }

  private updateSelectedExerciseField(field: 'totalTimeSec' | 'totalReps', value: number): void {
    const ctx = this.selectedExerciseContext();
    const plan = this.modifiedWorkoutPlan();
    if (!ctx || !plan) return;

    const dayNumber = parseInt(this.selectedDayTab());
    const dayIndex = plan.workoutPlan.days.findIndex(d => d.day === dayNumber);
    if (dayIndex < 0) return;

    const day = plan.workoutPlan.days[dayIndex];
    const sectionIndex = day.structure.findIndex(s => s.title === ctx.sectionTitle);
    if (sectionIndex < 0) return;

    const section = day.structure[sectionIndex];
    if (ctx.exerciseIndex < 0 || ctx.exerciseIndex >= section.exercises.length) return;

    const updatedExercises = section.exercises.map((ex, idx) =>
      idx === ctx.exerciseIndex ? { ...ex, [field]: value } : ex
    );

    // Warmup/Cardio/Cooldown durations are read-only and always derived in real time from
    // their exercises' time/reps data (totalTimeSec or totalReps * repDurationSec).
    const updatedDuration = this.isCalculatedTimeSection(ctx.sectionTitle)
      ? this.calculateTimeAndRepsDuration(updatedExercises, ctx.sectionTitle)
      : section.durationInMinutes;

    const updatedSection = { ...section, exercises: updatedExercises, durationInMinutes: updatedDuration };
    const newStructure = day.structure.map((s, idx) => idx === sectionIndex ? updatedSection : s);
    const newDay = { ...day, structure: newStructure };
    const newDays = plan.workoutPlan.days.map((d, idx) => idx === dayIndex ? newDay : d);

    this.modifiedWorkoutPlan.set({
      ...plan,
      workoutPlan: { ...plan.workoutPlan, days: newDays },
    });

    // Keep the open dialog's exercise reference in sync with the updated plan
    this.selectedExercise.set(updatedExercises[ctx.exerciseIndex]);
  }

  private touchStartTimeout: any = null;
  private touchMoved = false;

  onTouchStart(event: TouchEvent ): void {
    const target = event.currentTarget as HTMLElement;
    this.touchMoved = false;
    
    // Don't prevent default immediately - allow scrolling
    this.touchStartTimeout = setTimeout(() => {
      // Only trigger drag-ready if user hasn't scrolled
      if (!this.touchMoved) {
        target.classList.add('drag-ready');
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
      }
    }, 1000);
  }

  onTouchMove(event: TouchEvent): void {
    // Mark that user is scrolling
    this.touchMoved = true;
    const target = event.currentTarget as HTMLElement;
    // Clear timeout and remove drag-ready if scrolling
    if (this.touchStartTimeout) {
      clearTimeout(this.touchStartTimeout);
      this.touchStartTimeout = null;
    }
    target.classList.remove('drag-ready');
  }

  onTouchEnd(event: TouchEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (this.touchStartTimeout) {
      clearTimeout(this.touchStartTimeout);
      this.touchStartTimeout = null;
    }
    target.classList.remove('drag-ready');
    this.touchMoved = false;
  }

  // Prevent context menu on drag items
  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onExerciseDropped(event: CdkDragDrop<IExercise[]>, sectionTitle: string): void {
    const currentExercises = this.getSectionExercises(sectionTitle);
    if (!currentExercises) return;

    const reordered = [...currentExercises];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);

    this.updateSectionExercises(sectionTitle, reordered);
  }

  openExerciseActions(event: MouseEvent, sectionTitle: string, exerciseId: string, exerciseIndex: number): void {
    event.stopPropagation();
    this.selectedExerciseForActions.set({ sectionTitle, exerciseId, exerciseIndex });
    
    if (this.responsiveService.isMobileView()) {
      this.showExerciseActionsDrawer.set(true);
    } else {
      this.exerciseActionsPopover?.toggle(event);
    }
  }

  onDeleteSelectedExercise(): void {
    const ctx = this.selectedExerciseForActions();
    if (!ctx) return;

    const currentExercises = this.getSectionExercises(ctx.sectionTitle);
    if (!currentExercises) return;

    // Delete by index instead of ID to handle duplicate exercises correctly
    const remaining = currentExercises.filter((_, index) => index !== ctx.exerciseIndex);
    this.updateSectionExercises(ctx.sectionTitle, remaining);

    this.exerciseActionsPopover?.hide();
    this.showExerciseActionsDrawer.set(false);
    this.selectedExerciseForActions.set(null);
  }

  onSwitchExercisePlaceholder(): void {
    const ctx = this.selectedExerciseForActions();
    if (!ctx) return;

    // Find the exercise being switched to get its movement patterns
    const section = this.getSelectedDay()?.structure.find(s => s.title === ctx.sectionTitle);
    const exercise = section?.exercises.find(ex => ex.id === ctx.exerciseId);
    
    // Set the movement patterns for initial filtering
    this.selectedExerciseMovementPatterns.set(exercise?.movementPatterns || []);

    this.searchMode.set('switch');
    this.selectedSectionForSearch.set(ctx.sectionTitle);
    this.isSearchDialogVisible.set(true);
    this.exerciseActionsPopover?.hide();
    this.showExerciseActionsDrawer.set(false);
  }

  onAddExercise(sectionTitle: string): void {
    this.selectedExerciseMovementPatterns.set([]); // Clear movement patterns for add mode
    this.searchMode.set('add');
    this.selectedSectionForSearch.set(sectionTitle);
    this.isSearchDialogVisible.set(true);
  }

  onExercisesSelected(exercises: IExerciseSearchItem[]): void {
    const sectionTitle = this.selectedSectionForSearch();
    if (!sectionTitle || exercises.length === 0) return;

    if (this.searchMode() === 'add') {
      // Add all selected exercises
      exercises.forEach(exercise => {
        this.addExerciseToSection(sectionTitle, exercise);
      });
    } else {
      // Switch mode: only use the first exercise
      this.switchExerciseInSection(sectionTitle, exercises[0]);
    }
  }

  private addExerciseToSection(sectionTitle: string, exercise: IExerciseSearchItem): void {
    const currentExercises = this.getSectionExercises(sectionTitle);
    if (!currentExercises) return;
    const { totalTimeSec, totalReps } = this.getDefaultTimeOrReps(sectionTitle, exercise.isTimeBased);
    const newExercise: IExercise = {
      id: exercise.id,
      exerciseName: exercise.name,
      imagePath: exercise.imagePath,
      animationPath: exercise.animationPath,
      muscles: exercise.muscles,
      movementPatterns: exercise.movementPatterns || [],
      level: exercise.level,
      category: exercise.category,
      recommendedProtocol: null,
      tags: ['Manually Added'],
      order: currentExercises.length + 1,
      avgDuration: exercise.avgDuration,
      isTimeBased: exercise.isTimeBased,
      repDurationSec: exercise.repDurationSec,
      isPerSide: exercise.isPerSide,
      totalTimeSec,
      totalReps
    };

    const updatedExercises = [...currentExercises, newExercise];
    this.updateSectionExercises(sectionTitle, updatedExercises);
  }

  /**
   * Default totalTimeSec/totalReps for a newly added exercise in a Warmup, Cardio or Cooldown section.
   * - Time-based: Cardio defaults to 5 min (300 sec), Warmup/Cooldown default to 30 sec
   * - Reps-based: defaults to 10 reps
   * Non time/reps sections get null for both.
   */
  private getDefaultTimeOrReps(sectionTitle: string, isTimeBased?: boolean): { totalTimeSec: number | null; totalReps: number | null } {
    if (!this.isTimeOrRepsSection(sectionTitle)) {
      return { totalTimeSec: null, totalReps: null };
    }

    if (isTimeBased) {
      const isCardio = this.getSectionClass(sectionTitle) === 'cardio';
      return { totalTimeSec: isCardio ? 300 : 30, totalReps: null };
    }

    return { totalTimeSec: null, totalReps: 10 };
  }

  private switchExerciseInSection(sectionTitle: string, newExercise: IExerciseSearchItem): void {
    const ctx = this.selectedExerciseForActions();
    if (!ctx) return;

    const currentExercises = this.getSectionExercises(sectionTitle);
    if (!currentExercises) return;

    // Use index instead of ID to handle duplicate exercises correctly
    if (ctx.exerciseIndex < 0 || ctx.exerciseIndex >= currentExercises.length) return;

    const { totalTimeSec, totalReps } = this.getDefaultTimeOrReps(sectionTitle, newExercise.isTimeBased);
    const replacementExercise: IExercise = {
      id: newExercise.id,
      exerciseName: newExercise.name,
      imagePath: newExercise.imagePath,
      animationPath: newExercise.animationPath,
      muscles: newExercise.muscles,
      movementPatterns: newExercise.movementPatterns || [],
      level: newExercise.level,
      category: newExercise.category,
      recommendedProtocol: null,
      tags: ['Manually Added'],
      order: currentExercises[ctx.exerciseIndex].order,
      avgDuration: newExercise.avgDuration,
      isTimeBased: newExercise.isTimeBased,
      repDurationSec: newExercise.repDurationSec,
      isPerSide: newExercise.isPerSide,
      totalTimeSec,
      totalReps
    };

    const updatedExercises = currentExercises.map((ex, idx) =>
      idx === ctx.exerciseIndex ? replacementExercise : ex
    );

    this.updateSectionExercises(sectionTitle, updatedExercises);
  }

  onSearchDialogClose(): void {
    this.isSearchDialogVisible.set(false);
    this.selectedSectionForSearch.set(null);
  }
  

  private getSectionExercises(sectionTitle: string): IExercise[] | null {
    const plan = this.modifiedWorkoutPlan();
    if (!plan) return null;

    const dayNumber = parseInt(this.selectedDayTab());
    const day = plan.workoutPlan.days.find(d => d.day === dayNumber);
    if (!day) return null;

    const section = day.structure.find(s => s.title === sectionTitle);
    return section?.exercises ?? null;
  }


  private updateSectionExercises(sectionTitle: string, newExercises: IExercise[]): void {
    const plan = this.modifiedWorkoutPlan();
    if (!plan) return;

    const dayNumber = parseInt(this.selectedDayTab());
    const dayIndex = plan.workoutPlan.days.findIndex(d => d.day === dayNumber);
    if (dayIndex < 0) return;

    const day = plan.workoutPlan.days[dayIndex];
    const sectionIndex = day.structure.findIndex(s => s.title === sectionTitle);
    if (sectionIndex < 0) return;

    // Normalize order: 1, 2, 3, ...
    let normalizedExercises = newExercises.map((ex, idx) => ({
      ...ex,
      order: idx + 1,
    }));

    const section = day.structure[sectionIndex];
    const sectionClass = this.getSectionClass(sectionTitle);
    
    // Auto-calculate duration for Resistance Training / time/reps sections when exercises change
    let updatedDuration = section.durationInMinutes;
    if (sectionClass === 'resistance-training') {
      updatedDuration = this.calculateResistanceTrainingDuration(normalizedExercises);
    } else if (this.isCalculatedTimeSection(sectionTitle)) {
      updatedDuration = this.calculateTimeAndRepsDuration(normalizedExercises, sectionTitle);
    }

    // For editable sections, recalculate individual exercise durations
    if (this.isSectionDurationEditable(sectionTitle) && normalizedExercises.length > 0) {
      const individualDuration = updatedDuration / normalizedExercises.length;
      const roundedDuration = Math.round(individualDuration * 10) / 10;
      
      normalizedExercises = normalizedExercises.map(ex => ({
        ...ex,
        avgDuration: roundedDuration
      }));
    }

    // Rebuild structure immutably with updated exercises and duration
    const newStructure = day.structure.map((s, idx) =>
      idx === sectionIndex 
        ? { ...s, exercises: normalizedExercises, durationInMinutes: updatedDuration } 
        : s
    );

    const newDay = { ...day, structure: newStructure };
    const newDays = plan.workoutPlan.days.map((d, idx) =>
      idx === dayIndex ? newDay : d
    );

    // Update signal with new plan
    this.modifiedWorkoutPlan.set({
      ...plan,
      workoutPlan: {
        ...plan.workoutPlan,
        days: newDays,
      },
    });

    // Clear duplicate error when exercises are modified
    this.clearDuplicateError();
  }

  onSavePlan(): void {
    if (!this.permissionService.canEditWorkoutPlan()) {
      this.customToastService.showError('You do not have permission to edit workout plans');
      return;
    }
    const plan = this.modifiedWorkoutPlan();
    if (!plan) return;

    // Check for duplicate exercises
    const { duplicateKeys, firstDayWithDuplicate } = this.findDuplicateExercises(plan);
    if (duplicateKeys.size > 0) {
      this.duplicateExerciseIds.set(duplicateKeys);
      this.duplicateErrorMessage.set('Cannot save: Duplicate exercises detected within the same phase. Please remove or replace duplicate exercises.');

      if (firstDayWithDuplicate !== null) {
        this.selectedDayTab.set(firstDayWithDuplicate.toString());
      }

      // Scroll to the first duplicate exercise
      this.scrollToFirstDuplicate();
      return;
    }

    // Clear any previous duplicate errors
    this.duplicateExerciseIds.set(new Set());
    this.duplicateErrorMessage.set(null);

    const payload = buildWorkoutPlanUpdatePayload(plan, this.isReviewed());
    this.isSaving.set(true);
    this.workoutInfoService.updateWorkoutPlan(plan.id, payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.customToastService.showSuccess('Workout plan saved successfully');
        this.planSaved.emit();
        this.onClose();
      },
      error: () => {
        this.isSaving.set(false);
        this.customToastService.showError('Failed to save workout plan');
      }
    });
  }

  private findDuplicateExercises(plan: IWorkoutPlanResponse): { duplicateKeys: Set<string>; firstDayWithDuplicate: number | null } {
    const duplicateKeys = new Set<string>();
    let firstDayWithDuplicate: number | null = null;

    plan.workoutPlan.days.forEach(day => {
      day.structure.forEach(section => {
        const exerciseIdCounts = new Map<string, number>();

        section.exercises.forEach(exercise => {
          const count = exerciseIdCounts.get(exercise.id) || 0;
          exerciseIdCounts.set(exercise.id, count + 1);

          if (count + 1 > 1) {
            duplicateKeys.add(`${day.day}|${section.title}|${exercise.id}`);
            if (firstDayWithDuplicate === null) {
              firstDayWithDuplicate = day.day;
            }
          }
        });
      });
    });

    return { duplicateKeys, firstDayWithDuplicate };
  }

  private scrollToFirstDuplicate(): void {
    // Wait for Angular to render the highlighted elements
    setTimeout(() => {
      const firstDuplicateElement = document.querySelector('.exercise-duplicate-error');
      if (firstDuplicateElement) {
        firstDuplicateElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  }

  isDuplicateExercise(exerciseId: string, sectionTitle: string): boolean {
    const selectedDay = this.getSelectedDay();
    if (!selectedDay) return false;
    return this.duplicateExerciseIds().has(`${selectedDay.day}|${sectionTitle}|${exerciseId}`);
  }

  /**
   * Map workout plan response to calculate exercise durations for editable sections
   * and recalculate Resistance Training / Cardio section durations from exercises
   */
  private mapWorkoutPlanWithCalculatedDurations(response: IWorkoutPlanResponse): IWorkoutPlanResponse {
    response.workoutPlan.days.forEach(day => {
      day.structure.forEach(section => {
        const sectionClass = this.getSectionClass(section.title);
        if (this.isCalculatedTimeSection(section.title)) {
          // Warmup / Cardio / Cooldown durations are read-only: always the sum of their
          // exercises' time (totalTimeSec, or totalReps * repDurationSec for reps-based)
          section.durationInMinutes = this.calculateTimeAndRepsDuration(section.exercises, section.title);
        } else if (this.isSectionDurationEditable(section.title) && section.exercises.length > 0) {
          // Calculate for remaining editable (custom) sections
          const individualDuration = section.durationInMinutes / section.exercises.length;
          const roundedDuration = Math.round(individualDuration * 10) / 10;
          
          // Assign calculated duration to each exercise's avgDuration
          section.exercises.forEach(exercise => {
            exercise.avgDuration = roundedDuration;
          });
        } else if (sectionClass === 'resistance-training') {
          // For Resistance Training, recalculate duration from exercises
          section.durationInMinutes = this.calculateResistanceTrainingDuration(section.exercises);
        }
      });
    });
    
    return response;
  }

  clearDuplicateError(): void {
    this.duplicateExerciseIds.set(new Set());
    this.duplicateErrorMessage.set(null);
  }

  onResetPlan(): void {
    const original = this.workoutPlan();
    if (!original) return;
    this.modifiedWorkoutPlan.set(deepCloneWorkoutPlan(original));
  }

}
